import React, { CSSProperties, PureComponent } from 'react'
import { css, StyleSheet } from 'aphrodite/no-important'

import {
  ProfilePicture,
  ResidentSelectOption,
  SearchSelect,
} from '../../generic'
import { getNominative } from '../../../common/utils'
import { Resident } from '@lmc/ts-common'

interface ModalResidentSelectorProps {
  goToNextStep?: () => void
  data: {
    residents: Resident[]
  }
  setFormField: (data: { key: string; value: string }) => void
  style?: React.CSSProperties
  features: {
    preferredPeopleNominative: string
  }
}

export default class ModalResidentSelector extends PureComponent<ModalResidentSelectorProps> {
  handleSelect = (resident: Resident | null) => {
    const { goToNextStep, setFormField } = this.props
    if (!resident) return
    setFormField({ key: 'resident', value: resident.id })
    if (goToNextStep) goToNextStep()
  }

  renderResident = (resident: Resident) => (
    <div
      className={css(classes.selectableContainer)}
      key={resident.id}
      onClick={() => this.handleSelect(resident)}
      style={styles.resident}
    >
      <ProfilePicture
        className={`lmc-selectable-icon ${css(classes.selectableIcon)}`}
        url={resident.picture}
        size={100}
      />
      <span style={styles.residentName as CSSProperties}>
        {resident.name.first} {resident.name.last}
      </span>
    </div>
  )

  render(): JSX.Element {
    const {
      data: { residents },
      style,
    } = this.props
    const preferredResidentTerm = getNominative(
      this.props.features.preferredPeopleNominative,
      'Resident'
    )
    return (
      <div
        style={{
          ...style,
          ...styles.container,
        }}
      >
        <div style={styles.titleContainer as CSSProperties}>
          {`Choose a ${preferredResidentTerm}`}
        </div>
        <div style={styles.searchContainer}>
          <SearchSelect
            formatOptionLabel={ResidentSelectOption}
            getOptionLabel={(resident: Resident) =>
              `${resident.name.first} ${resident.name.last}`
            }
            getOptionValue={(resident: Resident) => resident.id}
            options={residents}
            placeholder="Search by name"
            onChange={this.handleSelect}
          />
        </div>
        <div
          style={styles.residentsContainer as CSSProperties}
          data-cy="residents-list"
        >
          {residents.map(this.renderResident)}
        </div>
      </div>
    )
  }
}

const classes = StyleSheet.create({
  selectableContainer: {
    cursor: 'pointer',
    ':hover .lmc-selectable-icon': {
      borderColor: 'rgba(255,255,255,0.5)',
    },
  },
  selectableIcon: {
    border: '3px solid rgba(255,255,255,0)',
    transition: 'border-color 0.2s ease-in-out',
  },
})

const styles = {
  container: {
    margin: '0 auto',
    maxWidth: 720,
  },
  resident: {
    cursor: 'pointer',
    margin: '0px 24px 24px',
  },
  residentName: {
    display: 'block',
    hyphens: 'auto',
    marginTop: 10,
    maxWidth: 100,
    textAlign: 'center',
    wordBreak: 'break-word',
  },
  residentsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    margin: '50px 0px 0px auto',
  },
  searchContainer: {
    paddingTop: 24,
  },
  titleContainer: {
    fontWeight: 300,
    fontSize: 28,
    textAlign: 'center',
  },
}
